import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import { AgreementsTable } from "@/components/customers/AgreementsTable";
import { AgreementFormDialog } from "@/components/customers/AgreementFormDialog";
import { useRecurringAgreements, useRunRecurringInvoices } from "@/hooks/use-recurring-agreements";
import { usePermissions } from "@/hooks/use-permissions";
import { computeMonthlyRunningRevenue } from "@/lib/recurring-agreements-calc";
import { formatNOK } from "@/lib/format";

export default function AgreementsPage() {
  const { data: agreements = [], isLoading, error } = useRecurringAgreements();
  const { isAdmin } = usePermissions();
  const runMotor = useRunRecurringInvoices();
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);

  const filtered = useMemo(
    () =>
      agreements.filter((a) => {
        if (typeFilter !== "all" && a.type !== typeFilter) return false;
        if (statusFilter !== "all" && a.status !== statusFilter) return false;
        return true;
      }),
    [agreements, typeFilter, statusFilter],
  );

  const monthlyRunningRevenue = useMemo(
    () => computeMonthlyRunningRevenue(agreements),
    [agreements],
  );

  if (error) {
    return (
      <div className="page-container">
        <p className="text-destructive">Feil ved lasting av avtaler: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="page-container animate-fade-in">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] flex items-center gap-2">
          <SrIcon name="repeat" className="h-6 w-6" />
          Avtaler
        </h1>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => runMotor.mutate()}
              disabled={runMotor.isPending}
              title="Kjør fakturamotoren manuelt (forfalte avtaler). Auto-send krever egen bryter."
            >
              <SrIcon
                name={runMotor.isPending ? "loader2" : "play"}
                className={runMotor.isPending ? "w-4 h-4 mr-2 animate-spin" : "w-4 h-4 mr-2"}
              />
              Kjør fakturamotor
            </Button>
          )}
          <Button onClick={() => setCreateOpen(true)}>
            <SrIcon name="plus" className="w-4 h-4 mr-2" /> Ny avtale
          </Button>
        </div>
      </div>

      <Card className="mb-4">
        <CardContent className="pt-6 flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Sum løpende inntekt (normalisert til måned):</span>
          <span className="font-display text-lg font-semibold">{formatNOK(monthlyRunningRevenue)}</span>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-40" aria-label="Filtrer på type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle typer</SelectItem>
                <SelectItem value="recurring">Løpende</SelectItem>
                <SelectItem value="rental">Utleie</SelectItem>
                <SelectItem value="storage">Lagring</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40" aria-label="Filtrer på status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle statuser</SelectItem>
                <SelectItem value="active">Aktiv</SelectItem>
                <SelectItem value="paused">Pause</SelectItem>
                <SelectItem value="ended">Avsluttet</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <AgreementsTable agreements={filtered} emptyMessage="Ingen avtaler matcher filteret." />
          )}
        </CardContent>
      </Card>

      <AgreementFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
